import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useContent } from "@/lib/content-context";
import { toast } from "sonner";
import { TextField, SectionCard, SaveBar, PageHeader } from "@/components/admin/Form";

export default function ThemeEditor() {
    const { content, refresh } = useContent();
    const [t, setT] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => { if (content) setT({ ...(content.theme || {}) }); }, [content]);
    if (!t) return null;

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/content/theme", { data: t });
            await refresh();
            toast.success("Theme saved. Refresh public site to see changes.");
        } catch { toast.error("Save failed"); }
        finally { setSaving(false); }
    };

    const ColorRow = ({ label, k }) => (
        <div className="flex items-center gap-4">
            <div
                className="w-12 h-12 border border-coffee/20 flex-shrink-0"
                style={{ background: t[k] || "#fff" }}
            />
            <div className="flex-1">
                <label className="tracked text-coffee/60 text-[10px] block mb-1">{label}</label>
                <input
                    type="text"
                    value={t[k] || ""}
                    onChange={(e) => setT({ ...t, [k]: e.target.value })}
                    className="w-full bg-transparent border-b border-coffee/30 focus:border-coffee py-2 text-coffee font-mono text-sm outline-none"
                    data-testid={`theme-${k}`}
                />
            </div>
        </div>
    );

    return (
        <div data-testid="theme-editor">
            <PageHeader subtitle="Theme & Brand" title="Colors, typography, identity" />

            <SectionCard title="Colors" description="Reference values used across the brand. Public site is built with Tailwind tokens — these are surfaced for documentation and future theming.">
                <ColorRow label="Primary (Deep Coffee)" k="primary" />
                <ColorRow label="Cream" k="cream" />
                <ColorRow label="Background (Warm Off-White)" k="background" />
                <ColorRow label="Gold Accent" k="gold" />
                <ColorRow label="Sage" k="sage" />
                <ColorRow label="Emerald" k="emerald" />
            </SectionCard>

            <SectionCard title="Typography">
                <TextField label="Headings Font" value={t.headings_font} onChange={(v) => setT({ ...t, headings_font: v })} testid="theme-heading-font" />
                <TextField label="Body Font" value={t.body_font} onChange={(v) => setT({ ...t, body_font: v })} testid="theme-body-font" />
                <TextField label="Editorial Serif Font" value={t.editorial_font} onChange={(v) => setT({ ...t, editorial_font: v })} testid="theme-editorial-font" />
            </SectionCard>

            <SaveBar onSave={save} saving={saving} label="Save Theme" />
        </div>
    );
}
